import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { ObjectException } from 'global/errors';
import { useFreecodeContextRun, useFreecodeContextData } from 'hooks/useContext';
import services from 'api/services';

const { ConfigService, FreecodeService } = services;

export default function useTestTrialLoadFunction() {
  const { isInitialized } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined);

  const { setTest, setTestImpl, setLanguageData, setTicket } = useFreecodeContextData();
  const { setLanguage, setCodeSol } = useFreecodeContextRun();

  useEffect(() => {
    if (!isInitialized) return;
    const fetchData = async () => {
      try {
        const [languageVersions, response] = await Promise.all([
          ConfigService.getLanguageVersions(),
          FreecodeService.loadFreecodeTrialTest(),
        ]);
        let { test, testImpl } = response;

        if (!test?._id) throw new ObjectException('Test missing from response', 'VALIDATION_ERROR');
        if (!testImpl?._id) throw new ObjectException('Implementation missing from response', 'VALIDATION_ERROR');
        if (test?._id !== testImpl?.test)
          throw new ObjectException('Test and implementation misssmatch', 'VALIDATION_ERROR');

        const allowedLanguages = languageVersions?.value?.[testImpl?.executionVersion];

        const foundLanguage =
          localStorage.getItem(`fsEditor-${test.category}-${test._id}-language`) ||
          (allowedLanguages['python'] ? 'python' : Object.keys(allowedLanguages)[0]);

        const foundCode =
          localStorage.getItem(`fsEditor-${test.category}-${test._id}-${foundLanguage}`) ||
          testImpl.generated?.skeletons?.[foundLanguage] ||
          'Code not found...';

        setTest(test);
        setTestImpl(testImpl);
        setLanguageData(allowedLanguages);
        setLanguage(foundLanguage);
        setCodeSol(foundCode);

        const timeLimit = 30;
        setTicket({
          timeLimit,
          startedAt: new Date(),
          deadlineAt: new Date(Date.now() + timeLimit * 60 * 1000),
          diffToServerTimeInSeconds: moment(new Date()).diff(test.serverTime, 'seconds'),
        });

        setLoading(false);
      } catch (err) {
        setError(err);
      }
    };
    fetchData();
  }, [isInitialized, setCodeSol, setLanguage, setLanguageData, setTest, setTestImpl, setTicket]);

  return { loading, error };
}
